import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Tabs, Tab } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import InfoIcon from '@mui/icons-material/Info';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import FunctionsIcon from '@mui/icons-material/Functions';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import LanIcon from '@mui/icons-material/Lan';
import SecurityIcon from '@mui/icons-material/Security';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ScienceIcon from '@mui/icons-material/Science';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import DescriptionIcon from '@mui/icons-material/Description';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GeneralInfoForm from './components/GeneralInfoForm.tsx';
import FinancialEntitiesPanel from './components/FinancialEntitiesPanel.tsx';
import BusinessServicesPanel from './components/BusinessServicesPanel.tsx';
import FunctionsPanel from './components/FunctionsPanel.tsx';
import { ICTThirdPartyProvidersPanel } from './components/ICTThirdPartyProvidersPanel.tsx';
import ICTServicesPanel from './components/ICTServicesPanel.tsx';
import ControlsPanel from './components/ControlsPanel.tsx';
import RisksPanel from './components/RisksPanel.tsx';
import IncidentsPanel from './components/IncidentsPanel.tsx';
import TestingExercisesPanel from './components/TestingExercisesPanel.tsx';
import FindingsPanel from './components/FindingsPanel.tsx';
import ContractualArrangementsPanel from './components/ContractualArrangementsPanel.tsx';
import { ICTServiceAssessmentsPanel } from './components/ICTServiceAssessmentsPanel.tsx';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

export interface DoraTabConfig {
  id: string;
  label: string;
  path: string;
  icon: React.ElementType;
  component: React.ReactNode;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`dora-tabpanel-${index}`}
    aria-labelledby={`dora-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

export const doraTabs: DoraTabConfig[] = [
  // General Info (organizational)
  { id: 'general-info', label: 'General Info', path: '/dora/general-info', icon: InfoIcon, component: <GeneralInfoForm /> },

  // B_01.02 - Entities and Consolidation
  { id: 'financial-entities', label: 'Financial Entities', path: '/dora/financial-entities', icon: AccountBalanceIcon, component: <FinancialEntitiesPanel /> },

  // B_02.02 - Contractual Arrangement Details
  { id: 'contractual-arrangements', label: 'Contractual Arrangements', path: '/dora/contractual-arrangements', icon: DescriptionIcon, component: <ContractualArrangementsPanel /> },

  // B_05.01 - ICT Third-Party Service Providers
  { id: 'ict-third-party-providers', label: 'ICT Third-Party Providers', path: '/dora/ict-third-party-providers', icon: Diversity3Icon, component: <ICTThirdPartyProvidersPanel /> },

  // B_06.01 - Function Identification
  { id: 'functions', label: 'Functions', path: '/dora/functions', icon: FunctionsIcon, component: <FunctionsPanel /> },

  // B_07.01 - ICT Service Assessments
  { id: 'ict-service-assessments', label: 'ICT Service Assessments', path: '/dora/ict-service-assessments', icon: AssessmentIcon, component: <ICTServiceAssessmentsPanel /> },

  // Other panels
  { id: 'business-services', label: 'Business Services', path: '/dora/business-services', icon: BusinessCenterIcon, component: <BusinessServicesPanel /> },
  { id: 'ict-services', label: 'ICT Services', path: '/dora/ict-services', icon: LanIcon, component: <ICTServicesPanel /> },
  { id: 'controls', label: 'Controls', path: '/dora/controls', icon: SecurityIcon, component: <ControlsPanel /> },
  { id: 'risks', label: 'Risks', path: '/dora/risks', icon: WarningAmberIcon, component: <RisksPanel /> },
  { id: 'incidents', label: 'Incidents', path: '/dora/incidents', icon: ReportProblemIcon, component: <IncidentsPanel /> },
  { id: 'testing-exercises', label: 'Testing Exercises', path: '/dora/testing-exercises', icon: ScienceIcon, component: <TestingExercisesPanel /> },
  { id: 'findings', label: 'Findings', path: '/dora/findings', icon: FactCheckIcon, component: <FindingsPanel /> },
];

interface DoraModuleProps {
  initialTabId?: string;
}

export const DoraModule: React.FC<DoraModuleProps> = ({ initialTabId }) => {
  const navigate = useNavigate();

  const initialIndex = useMemo(() => {
    const idx = doraTabs.findIndex(tab => tab.id === initialTabId);
    return idx >= 0 ? idx : 0;
  }, [initialTabId]);

  const [tabValue, setTabValue] = useState(initialIndex);

  useEffect(() => {
    setTabValue(initialIndex);
  }, [initialIndex]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    navigate(doraTabs[newValue]?.path ?? '/dora');
  };

  const activeTab = doraTabs[tabValue] ?? doraTabs[0];

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" gutterBottom>
        Digital Operational Resilience Act (DORA)
      </Typography>
      <Typography variant="subtitle1" sx={{ mb: 2 }} color="text.secondary">
        {`Currently viewing: ${activeTab.label}`}
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="dora tabs" variant="scrollable" scrollButtons allowScrollButtonsMobile>
          {doraTabs.map((tab, index) => (
            <Tab key={tab.label} label={tab.label} id={`dora-tab-${index}`} />
          ))}
        </Tabs>
      </Box>

      {doraTabs.map((tab, index) => (
        <TabPanel key={tab.label} value={tabValue} index={index}>
          {tab.component}
        </TabPanel>
      ))}
    </Box>
  );
};

export default DoraModule;
